<template>
  <div class="min-h-screen bg-gray-50 p-8 font-sans text-gray-800">
    <div class="mx-auto max-w-7xl rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-100">

      <!-- Header Section with Title and Search Bar -->
      <div
          class="mb-6 flex flex-col items-start justify-between gap-4 border-b border-gray-100 pb-5 sm:flex-row sm:items-center">
        <div>
          <h2 class="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-2xl font-bold text-transparent">
            Aluminium Profiles
          </h2>
        </div>

        <!-- NEW: Real-time Search Bar -->
        <div class="relative w-full sm:w-72">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search from all columns..."
            class="block w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div class="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table class="min-w-full divide-y divide-gray-200 bg-white text-left text-sm">
          <thead class="bg-gray-50">
          <tr>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600">S/N</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600">Sunrack Code</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600 w-48">Generic Name</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600 w-48">Profile Name</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600">Unit Weight</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600">
              <div class="flex flex-col">
                <span>Profile Dimensions</span>
                <span class="text-xs font-normal text-gray-400">(Downloads)</span>
              </div>
            </th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600">Extruder Codes</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600">Profile Status</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600 w-24">Last Updated</th>
            <th scope="col" class="px-3 py-3 font-semibold text-gray-600 text-center">Modify</th>
          </tr>
          </thead>

          <tbody class="divide-y divide-gray-100">
          <!-- CHANGED: Now iterating over filteredProfiles instead of profiles -->
          <tr
              v-for="(profile, index) in filteredProfiles"
              :key="profile.id"
              class="group transition-all duration-300 hover:bg-blue-50/50 hover:shadow-inner"
          >
            <td class="px-3 py-3 font-medium text-gray-900">{{ index + 1 }}</td>

            <td class="px-3 py-3 font-mono text-blue-600">
              <span
                  class="cursor-pointer hover:underline"
                  @mouseenter="showImagePreview(profile.profileImage, $event)"
                  @mouseleave="hideImagePreview"
                  @mousemove="updateImagePosition"
              >
                {{ profile.sunrackCode }}
              </span>
            </td>

            <td class="px-3 py-3 w-48 max-w-[12rem]">
              <div class="line-clamp-2 cursor-pointer transition-colors hover:text-blue-700"
                   :title="profile.genericName">
                {{ profile.genericName }}
              </div>
            </td>

            <td class="px-3 py-3 w-48 max-w-[12rem]">
              <div class="line-clamp-2 cursor-pointer transition-colors hover:text-blue-700"
                   :title="profile.profileName">
                {{ profile.profileName }}
              </div>
            </td>

            <td class="px-3 py-3 text-gray-600">{{ profile.unitWeight }} <span class="text-xs">kg/m</span></td>

            <td class="px-3 py-3">
              <div class="flex items-center gap-3">
                <a v-if="profile.pdfLink" :href="profile.pdfLink" target="_blank"
                   class="text-blue-600 text-sm hover:underline">PDF</a>
                <a v-if="profile.dwgLink" :href="profile.dwgLink" target="_blank"
                   class="text-blue-600 text-sm hover:underline">DWG</a>
                <span v-if="!profile.pdfLink && !profile.dwgLink" class="text-xs text-gray-400">No files</span>
              </div>
            </td>

            <td class="px-3 py-3">
                <span class="text-gray-600">
                  {{ profile.extruderCode }}
                </span>
            </td>
            <td class="px-3 py-3">
              <span
                  :class="[profile.status === 'Active' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/10', 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset']">
                {{ profile.status }}
              </span>
            </td>

            <td class="px-3 py-3 text-xs text-gray-500 w-24">
              {{ profile.lastUpdated }}
            </td>

            <td class="px-3 py-3 text-center">
              <div class="flex items-center justify-center gap-2 opacity-80 transition-opacity group-hover:opacity-100">
                <router-link :to="{ name: 'Edit_alu_Profile', params: { sr: profile.sr } }">
                  <button
                      class="rounded-lg p-1.5 text-blue-600 transition-colors hover:bg-blue-100" title="Edit">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                </router-link>
                <button @click="confirmDelete(profile)"
                        class="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-100" title="Delete">
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
          </tbody>
        </table>

        <button
            type="button" @click="goTo('Add_alu_Profile')"
            class="rounded-lg bg-blue-600 px-4 py-2.5 m-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Add Profile
        </button>

        <!-- CHANGED: Empty state now reflects search results too -->
        <div v-if="filteredProfiles.length === 0" class="flex flex-col items-center justify-center py-12 text-gray-500">
          <svg class="mb-3 h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
          </svg>
          <p v-if="searchQuery">No profiles match "{{ searchQuery }}".</p>
          <p v-else>No aluminium profiles found.</p>
        </div>
      </div>
    </div>

    <div
        v-if="hoveredImage"
        class="pointer-events-none fixed z-50 overflow-hidden rounded-xl border border-gray-200 bg-white p-2 shadow-2xl transition-transform"
        :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }"
    >
      <img :src="hoveredImage" alt="Profile Preview" class="h-40 w-40 object-contain"/>
    </div>

    <Transition name="fade">
      <div v-if="isDeleteModalOpen"
           class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
        <div class="w-full max-w-md scale-100 transform rounded-2xl bg-white p-6 shadow-2xl transition-transform">
          <div class="mb-4 flex items-center gap-3">
            <div
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900">Delete Profile</h3>
          </div>
          <p class="text-sm text-gray-600">
            Are you sure you want to delete <span class="font-bold text-gray-900">{{
              profileToDelete?.profileName
            }}</span> ({{ profileToDelete?.sunrackCode }})? This action cannot be undone.
          </p>
          <div class="mt-6 flex justify-end gap-3">
            <button @click="isDeleteModalOpen = false"
                    class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">
              Cancel
            </button>
            <button @click="deleteprofile(profileToDelete.id)"
                    class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700">
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: 'Aluminium_dash',
  data() {
    return {
      searchQuery: '', // NEW: Tracks the search input
      lastUpdated: new Date().toLocaleString('en-IN', {dateStyle: 'medium', timeStyle: 'short'}),
      isDeleteModalOpen: false,
      profileToDelete: null,
      profiles: [],
      hoveredImage: null,
      tooltipX: 0,
      tooltipY: 0,
    };
  },
  computed: {
    // NEW: Real-time filtering logic
    filteredProfiles() {
      // If the search bar is empty, return all profiles
      if (!this.searchQuery) return this.profiles;

      const query = this.searchQuery.toLowerCase();

      // Return only profiles that match the query in ANY of these columns
      return this.profiles.filter(profile => {
        return (
          (profile.sunrackCode && profile.sunrackCode.toLowerCase().includes(query)) ||
          (profile.genericName && profile.genericName.toLowerCase().includes(query)) ||
          (profile.profileName && profile.profileName.toLowerCase().includes(query)) ||
          (profile.extruderCode && profile.extruderCode.toLowerCase().includes(query)) ||
          (profile.status && profile.status.toLowerCase().includes(query)) ||
          (profile.unitWeight && String(profile.unitWeight).toLowerCase().includes(query)) ||
          (profile.lastUpdated && profile.lastUpdated.toLowerCase().includes(query))
        );
      });
    }
  },
  mounted() {
    this.dispData()
  },
  methods: {
    showImagePreview(imageUrl, event) {
      if (!imageUrl) return;
      this.hoveredImage = imageUrl;
      this.tooltipX = event.clientX + 15;
      this.tooltipY = event.clientY + 15;
    },
    hideImagePreview() {
      this.hoveredImage = null;
    },
    updateImagePosition(event) {
      if (!this.hoveredImage) return;
      this.tooltipX = event.clientX + 15;
      this.tooltipY = event.clientY + 15;
    },
    confirmDelete(profile) {
      this.profileToDelete = profile;
      this.isDeleteModalOpen = true;
    },
    goTo(route) {
      this.$router.push({name: route});
    },
    async deleteprofile(id) {
      if (!id) {
        console.error("No ID provided for deletion!");
        return;
      }
      try {
        let result = await axios.delete(`alumium_db/${id}/`);
        if (result.status === 200 || result.status === 204) {
          this.dispData();
          this.isDeleteModalOpen = false;
          this.profileToDelete = null;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async dispData() {
      try {
        let result = await axios.get('alumium_db/');
        console.log("Result -", result.data);

        this.profiles = result.data.map(item => ({
          id: item.id,
          sr: item.sr,
          sunrackCode: item.sunrack_code,
          profileImage: item.profile_image,
          genericName: item.generic_name,
          profileName: item.profile_name,
          unitWeight: item.unit_weight,
          pdfLink: item.profile_dime_pdf,
          dwgLink: item.profile_dime_dwg,
          extruderCode: item.extruder_codes,
          status: item.profile_status,
          lastUpdated: item.last_updated
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>